import CrowdExpressionItem from './CrowdExpressionItem';
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';

const CrowdExpressionList = ({ items }) => {
  const audioRefs = useRef(new Map());
  const audioContextRef = useRef(null);
  const analyserRefs = useRef(new Map());
  const sourceRefs = useRef(new Map());
  const rafRefs = useRef(new Map());

  const [activeTrackId, setActiveTrackId] = useState(null);
  const [trackState, setTrackState] = useState({});

  useEffect(() => {
    setTrackState((prev) => {
      const next = {};

      for (const item of items) {
        next[item.id] = prev[item.id] ?? {
          isActive: false,
          audioProgress: 0,
          waveformData: [],
        };
      }

      return next;
    });
  }, [items]);
  const stopAnimation = useCallback((trackId) => {
    const rafId = rafRefs.current.get(trackId);
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafRefs.current.delete(trackId);
    }
  }, []);

  const ensureAudioGraph = useCallback(async (trackId) => {
    const audio = audioRefs.current.get(trackId);
    if (!audio) return;

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextClass();
    }

    if (!analyserRefs.current.has(trackId)) {
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 256;
      analyserRefs.current.set(trackId, analyser);
    }

    if (!sourceRefs.current.has(trackId)) {
      const source = audioContextRef.current.createMediaElementSource(audio);
      source.connect(analyserRefs.current.get(trackId));
      analyserRefs.current.get(trackId).connect(audioContextRef.current.destination);
      sourceRefs.current.set(trackId, source);
    }

    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
  }, []);

  const updateFrame = useCallback((trackId) => {
    const audio = audioRefs.current.get(trackId);
    const analyser = analyserRefs.current.get(trackId);

    if (!audio || !analyser) return;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      if (!audio || audio.paused || audio.ended) return;

      analyser.getByteFrequencyData(dataArray);

      setTrackState((prev) => ({
        ...prev,
        [trackId]: {
          isActive: true,
          audioProgress: audio.duration ? audio.currentTime / audio.duration : 0,
          audioCurrentTime: audio ? audio.currentTime : 0,
          waveformData: Array.from(dataArray),
        },
      }));

      rafRefs.current.set(trackId, requestAnimationFrame(tick));
    };

    tick();
  }, []);

  const pauseAllExcept = useCallback((exceptId) => {
    for (const [trackId, audio] of audioRefs.current.entries()) {
      if (trackId !== exceptId) {
        audio.pause();
      }
    }
  }, []);

  const playTrack = useCallback(
    async (track) => {
      const audio = audioRefs.current.get(track.id);
      if (!audio) return;

      pauseAllExcept(track.id);
      stopAnimation(activeTrackId);

      setActiveTrackId(track.id);

      await ensureAudioGraph(track.id);

      if (audio.src !== track.url) {
        audio.src = track.url;
        audio.load();
      }

      await audio.play();
    },
    [activeTrackId, ensureAudioGraph, pauseAllExcept, stopAnimation]
  );

  const pauseTrack = useCallback((trackId) => {
    const audio = audioRefs.current.get(trackId);
    if (!audio) return;

    audio.pause();
    stopAnimation(trackId);

    setTrackState((prev) => ({
      ...prev,
      [trackId]: {
        ...(prev[trackId] ?? {}),
        isActive: false,
      },
    }));

    if (activeTrackId === trackId) {
      setActiveTrackId(null);
    }
  }, [activeTrackId, stopAnimation]);

  const registerAudio = useCallback((trackId, audioEl) => {
    if (audioEl) {
      audioRefs.current.set(trackId, audioEl);

      const handlePlay = () => updateFrame(trackId);
      const handlePause = () => stopAnimation(trackId);
      const handleEnded = () => {
        stopAnimation(trackId);
        setTrackState((prev) => ({
          ...prev,
          [trackId]: {
            ...(prev[trackId] ?? {}),
            isActive: false,
            audioProgress: 0,
            waveformData: [],
          },
        }));

        setActiveTrackId((current) => (current === trackId ? null : current));
      };

      audioEl.addEventListener('play', handlePlay);
      audioEl.addEventListener('pause', handlePause);
      audioEl.addEventListener('ended', handleEnded);

      audioEl._crowdHandlers = { handlePlay, handlePause, handleEnded };
      return;
    }

    const existing = audioRefs.current.get(trackId);
    if (existing?._crowdHandlers) {
      const { handlePlay, handlePause, handleEnded } = existing._crowdHandlers;
      existing.removeEventListener('play', handlePlay);
      existing.removeEventListener('pause', handlePause);
      existing.removeEventListener('ended', handleEnded);
    }

    audioRefs.current.delete(trackId);
    stopAnimation(trackId);
    analyserRefs.current.delete(trackId);
    sourceRefs.current.delete(trackId);
    setTrackState((prev) => {
      const next = { ...prev };
      delete next[trackId];
      return next;
    });
  }, [stopAnimation, updateFrame]);

  return (
    <ul className="w-full max-w-5xl grid gap-2 list-none m-0 pt-0 pb-0 pl-0 pr-2 box-border 
      overflow-y-auto overflow-x-hidden max-h-[calc(70vh-120px)] sm:max-h-[calc(60vh-100px)] 
      khj-scrollbar">
      {items.map((item) => {
        const state = trackState[item.id] ?? {
          isActive: false,
          audioProgress: 0,
          audioCurrentTime: 0,
          waveformData: [],
        };

        return (
          <CrowdExpressionItem
            key={item.id}
            item={item}
            isActive={activeTrackId === item.id}
            onRegisterAudio={registerAudio}
            onToggle={playTrack}
            onPause={pauseTrack}
            audioProgress={state.audioProgress}
            audioCurrentTime={state.audioCurrentTime}
            waveformData={state.waveformData}
          />
        );
      })}
    </ul>
  );
};

export default CrowdExpressionList;
